import * as Yup from 'yup';

import {
  EnterpriseRatingSeverity,
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  IUnderwritingQuoteExcessLayerParticipant,
  IUnderwritingQuoteExcessTowerLayer,
  SignificantTermsConditions,
  addMetadataToCoverage,
  addMetadataToEndorsements,
  sortCoveragesByType,
} from '@converge/graphql';
import { IRaterContextState, IUnderwritingSocketOutput } from '../types';
import config, { convergeLogger } from '@converge/config';
import { handleError, isNotNullOrUndefined } from '@converge/utils';

import { captureException } from '@sentry/react';
import { getEndorsementRaterPropertyOrFallback } from './rater.upsert-quote-variables.create';
import { isConverge } from '@converge/data';

const PERCENT_VALIDATOR = Yup.number().min(0).max(1);

const PRIMARY_PARTICIPANTS_SCHEMA = Yup.object()
  .shape({
    uuid: Yup.string().required(),
    companyShare: Yup.number().min(0).max(1).required(),
    attachmentPoint: Yup.number().min(0).required(),
    minimumPremium: Yup.number().min(0).nullable(),
    benchmarkPremium: Yup.number().nullable(),
    boundPremium: Yup.number().min(0).required(),
    boundUnderlyingPercent: PERCENT_VALIDATOR.nullable(),
    boundIncreasedLimitFactor: PERCENT_VALIDATOR.nullable(),
  })
  .required();

const EXCESS_PARTICIPANTS_SCHEMA = Yup.object()
  .shape({
    uuid: Yup.string().required(),
    // quote: Yup.object()
    //   .shape({
    companyShare: PERCENT_VALIDATOR.required(),
    attachmentPoint: Yup.number().min(0).required(),
    minimumPremium: Yup.number().min(0).required(),
    benchmarkPremium: Yup.number().min(0).required(),
    boundPremium: Yup.number().min(0).required(),
    boundUnderlyingPercent: PERCENT_VALIDATOR.required(),
    boundIncreasedLimitFactor: PERCENT_VALIDATOR.required(),
    // })
    // .required(),
  })
  .required();

const RATER_MESSAGE_SCHEMA = Yup.object().shape({
  type: Yup.string().oneOf(['coverage_rating_response']).required(),
  results: Yup.object().shape({
    minimumPremium: Yup.number().required(),
    limit: Yup.number().required(),
    modifiedCoveragePremium: Yup.number().required(),
    quotedPremium: Yup.number().required(),
    technicalPremium: Yup.number().required(),
    ratedPremium: Yup.number().required(),
    retention: Yup.number().required(),
    significantTermsConditions: Yup.object()
      .shape({
        endorsementImpact: Yup.string()
          .oneOf(Object.values(SignificantTermsConditions))
          .required(),
        selectedModifier: Yup.number().nullable(),
        ratedModifier: Yup.number().required(),
        modifier: Yup.number().required(),
        minModifier: Yup.number().required(),
        maxModifier: Yup.number().required(),
      })
      .required(),
    scheduleRating: Yup.object()
      .shape({
        modifier: Yup.number().required(),
        percentage: Yup.number().required(),
        minPercentage: Yup.number().required(),
        maxPercentage: Yup.number().required(),
        ratings: Yup.array()
          .of(
            Yup.object().shape({
              uuid: Yup.string().required(),
              percentage: Yup.number().required(),
              minPercentage: Yup.number().required(),
              maxPercentage: Yup.number().required(),
            })
          )
          .required(),
      })
      .required(),
    enterpriseRating: Yup.object()
      .shape({
        fusionScore: Yup.number().required(),
        ratedFusionScoreModifier: Yup.number().required(),
        selectedFusionScoreModifier: Yup.number().nullable(),
        minFusionScoreModifier: Yup.number().required(),
        maxFusionScoreModifier: Yup.number().required(),
        fusionScoreModifier: Yup.number().required(),
        riskLevels: Yup.array()
          .of(
            Yup.object().shape({
              uuid: Yup.string().required(),
              riskLevel: Yup.string()
                .oneOf(Object.values(EnterpriseRatingSeverity))
                .required(),
              score: Yup.number().required(),
            })
          )
          .required(),
      })
      .required(),
    coverages: Yup.array()
      .of(
        Yup.object()
          .shape({
            uuid: Yup.string().required(),
            minFusionScoreModifier: Yup.number().required(),
            maxFusionScoreModifier: Yup.number().required(),
            basePremium: Yup.number().required(),
            technicalPremium: Yup.number().required(),
            modifiedCoveragePremium: Yup.number().required(),
            fusionScore: Yup.number().required(),
            fusionScoreModifier: Yup.number().required(),
            ratedFusionScoreModifier: Yup.number().required(),
            limitRetentionFactor: Yup.number().required(),
            policyTermFactor: Yup.number().required(),
            priorActsFactor: Yup.number().required(),
            restorationPeriodFactor: Yup.number().required(),
            waitingPeriodFactor: Yup.number().required(),
            commissionAdjustmentFactor: Yup.number().required(),
            sharedCoverageAggregateFactor: Yup.number().required(),
            sharedPolicyAggregateFactor: Yup.number().required(),
            enhancedMediaFactor: Yup.number().required(),
            subCoverageFactor: Yup.number().required(),
            hazardGroup: Yup.number().required(),
            // waitingPeriodHours: Yup.number().required(),
            // waitingPeriodDays: Yup.number().required(),
            // restorationPeriodDays: Yup.number().required(),
          })
          .required()
      )
      .required(),
    endorsements: Yup.array()
      .of(
        Yup.object()
          .shape({
            uuid: Yup.string().required(),
            minFusionScoreModifier: Yup.number().required(),
            maxFusionScoreModifier: Yup.number().required(),
            basePremium: Yup.number().required(),
            technicalPremium: Yup.number().required(),
            modifiedCoveragePremium: Yup.number().required(),
            fusionScoreModifier: Yup.number().required(),
            ratedFusionScoreModifier: Yup.number().required(),
            limitRetentionFactor: Yup.number().required(),
            policyTermFactor: Yup.number().required(),
            priorActsFactor: Yup.number().required(),
            restorationPeriodFactor: Yup.number().required(),
            waitingPeriodFactor: Yup.number().required(),
            commissionAdjustmentFactor: Yup.number().required(),
            sharedCoverageAggregateFactor: Yup.number().required(),
            sharedPolicyAggregateFactor: Yup.number().required(),
            enhancedMediaFactor: Yup.number().required(),
            subCoverageFactor: Yup.number().required(),
            hazardGroup: Yup.number()
              .when('nonPremiumBearing', {
                is: true,
                then: (schema) => schema.nullable(),
                otherwise: (schema) => schema.required(),
              })
              .required(),
            fusionScore: Yup.number()
              .when('nonPremiumBearing', {
                is: true,
                then: (schema) => schema.nullable(),
                otherwise: (schema) => schema.required(),
              })
              .required(),
            // waitingPeriodHours: Yup.number().required(),
            // waitingPeriodDays: Yup.number().required(),
            // restorationPeriodDays: Yup.number().required(),
            // })
            // .required(),
          })
          .required()
      )
      .required(),
    excess: Yup.object()
      .shape({
        convergeAttachmentPoint: Yup.number().required(),
        convergeIncreasedLimitFactor: Yup.number().required(),
        convergeLimit: Yup.number().nullable(),
        convergePartOfLimit: Yup.number().required(),
        convergeQuotaShare: Yup.number().required(),
        convergeQuotedPremium: Yup.number().required(),
        convergeQuotedToBenchmarkRatio: Yup.number().required(),
        convergeRatedPremium: Yup.number().required(),
        convergeUnderlyingPremiumPercent: Yup.number().required(),
        correctiveFactor: Yup.number().required(),
        maxCorrectiveFactor: Yup.number().required(),
        minCorrectiveFactor: Yup.number().required(),
        convergeQuotedRatedRatio: Yup.number().required(),
        selectedCorrectiveFactor: Yup.number().required(),
        // erMaxFusionScoreModifier: Yup.number().required(),
        // erMinFusionScoreModifier: Yup.number().required(),
        // erRatedFusionScoreModifier: Yup.number().required(),
        // erFusionScoreModifier: Yup.number().required(),
        // erFusionScore: Yup.number().required(),

        scheduleRating: Yup.object()
          .shape({
            modifier: Yup.number().required(),
            percentage: Yup.number().required(),
            minPercentage: Yup.number().required(),
            maxPercentage: Yup.number().required(),
            ratings: Yup.array()
              .of(
                Yup.object().shape({
                  uuid: Yup.string().required(),
                  percentage: Yup.number().required(),
                  minPercentage: Yup.number().required(),
                  maxPercentage: Yup.number().required(),
                  modifier: Yup.number().required(),
                })
              )
              .required(),
          })
          .required(),
        significantTermsConditions: Yup.object()
          .shape({
            maxModifier: Yup.number().required(),
            minModifier: Yup.number().required(),
            modifier: Yup.number().required(),
            ratedModifier: Yup.number().required(),
            selectedModifier: Yup.number().nullable(),
          })
          .required(),
        tower: Yup.array()
          .of(
            Yup.object()
              .shape({
                name: Yup.string().required(),
                position: Yup.number().required(),
                attachmentPoint: Yup.number().min(0).required(),
                minimumPremium: Yup.number().when('position', {
                  is: 0,
                  then: (schema) => schema.nullable(),
                  otherwise: (schema) => schema.required(),
                }),
                benchmarkPremium: Yup.number().min(0).required(),
                boundPremium: Yup.number().min(0).required(),
                boundUnderlyingPercent: Yup.number()
                  .when('position', {
                    is: 0,
                    then: (schema) => schema.nullable(),
                    otherwise: (schema) => schema.required(),
                  })
                  .min(0)
                  .max(1),
                boundIncreasedLimitFactor: Yup.number()
                  .when('position', {
                    is: 0,
                    otherwise: (schema) => schema.required(),
                    then: (schema) => {
                      return schema.nullable();
                    },
                  })
                  .min(0)
                  .max(1),
                companyShare: Yup.number()
                  .when('position', {
                    is: 0,
                    then: (schema) => schema.nullable(),
                    otherwise: (schema) => schema.required(),
                  })
                  .min(0)
                  .max(1),
                participants: Yup.array().when('position', {
                  is: 0,
                  then: (schema) => schema.of(PRIMARY_PARTICIPANTS_SCHEMA),
                  otherwise: (schema) => schema.of(EXCESS_PARTICIPANTS_SCHEMA),
                }),
              })
              .test((info, meta) => {
                const foundIndex = meta.path.match(/\[(\d+?)\]$/)?.[1];
                const index = parseInt(foundIndex ?? '-1', 10);
                const doesPositionMatchIndex = info.position === index;
                return doesPositionMatchIndex
                  ? true
                  : meta.createError({
                      path: `${meta.path}position`,
                      message: `Position for excess layer ${info.position} does not match index ${index} (${foundIndex} ${meta.path})`,
                    });
              })
              .required()
          )
          .required(),
      })
      .nullable(),
  }),
});

const THROW_VALIDATION_ERRORS = false;

export const receiveRaterMessageAndUpdateState = (
  state: IRaterContextState,
  inputData: IUnderwritingSocketOutput
) => {
  if (config.featureFlags.enableRaterMessageValidation) {
    try {
      RATER_MESSAGE_SCHEMA.validateSync(inputData, {
        abortEarly: false,
        strict: true,
      });
    } catch (cause) {
      if (cause instanceof Yup.ValidationError) {
        convergeLogger.warn(cause.message, cause.inner, inputData);

        handleError({
          message: `Invalid rater response: ${cause.message}`,
          cause,
          source: 'receiveRaterMessageAndUpdateState',
          context: {
            validationErrors: cause.inner.reduce(
              (errors, error) => ({
                ...errors,
                [error.path ?? 'unknown path']: error.message,
              }),
              {}
            ),
            response: {
              ...inputData,
            },
          },
        });

        if (THROW_VALIDATION_ERRORS) {
          throw cause.inner[0] ?? cause;
        }
      }
    }
  }

  const data = inputData;
  const srPercentage =
    typeof data.results.scheduleRating.percentage === 'number'
      ? // TODO: Figure out why we are multiplying by 100 here
        data.results.scheduleRating.percentage * 100
      : state.quote.srPercentage;

  const srModifier =
    data.results.scheduleRating.modifier ?? state.quote.srModifier;

  const srMinPercentage =
    typeof data.results.scheduleRating.minPercentage === 'number'
      ? data.results.scheduleRating.minPercentage * 100
      : state.quote.srMinPercentage;

  const srMaxPercentage =
    typeof data.results.scheduleRating.maxPercentage === 'number'
      ? data.results.scheduleRating.maxPercentage * 100
      : state.quote.srMaxPercentage;

  const srMinItemPercentage =
    typeof data.results.scheduleRating.ratings?.[0]?.minPercentage === 'number'
      ? data.results.scheduleRating.ratings[0].minPercentage * 100
      : state.quote.srMinItemPercentage;

  const srMaxItemPercentage =
    typeof data.results.scheduleRating.ratings?.[0]?.maxPercentage === 'number'
      ? data.results.scheduleRating.ratings[0].maxPercentage * 100
      : state.quote.srMaxItemPercentage;

  const erMaxFusionScoreModifier =
    data.results.enterpriseRating.maxFusionScoreModifier ??
    config.defaults.enterpriseRatingMaxFusionScoreModifier;

  const erMinFusionScoreModifier =
    data.results.enterpriseRating.minFusionScoreModifier ??
    config.defaults.enterpriseRatingMinFusionScoreModifier;

  const erRatedFusionScoreModifier =
    data.results.enterpriseRating.ratedFusionScoreModifier ??
    config.defaults.enterpriseRatingFusionScoreModifier;

  const erFusionScoreModifier =
    data.results.enterpriseRating.fusionScoreModifier ?? null;

  const erFusionScore = data.results.enterpriseRating.fusionScore;

  /* Significant terms & conditions */
  const stcMinModifier =
    data.results.significantTermsConditions?.minModifier ??
    config.defaults.significantTermsConditionsMinModifier;
  const stcMaxModifier =
    data.results.significantTermsConditions?.maxModifier ??
    config.defaults.significantTermsConditionsMaxModifier;
  const stcRatedModifier =
    data.results.significantTermsConditions?.ratedModifier ?? null;
  const stcModifier = data.results.significantTermsConditions?.modifier ?? null;

  const changedQuoteFields: string[] = [];

  if (
    state.quote.selectedQuotedPremium !== data.results.selectedQuotedPremium
  ) {
    changedQuoteFields.push('premium');
  }
  if (state.quote.quotedRatedRatio !== data.results.quotedRatedRatio) {
    changedQuoteFields.push('quotedRatedRatio');
  }

  if (state.quote.technicalPremium !== data.results.technicalPremium) {
    changedQuoteFields.push('technicalPremium');
  }

  if (state.quote.ratedPremium !== data.results.ratedPremium) {
    changedQuoteFields.push('ratedPremium');
  }

  if (state.quote.minimumPremium !== data.results.minimumPremium) {
    changedQuoteFields.push('minimumPremium');
  }

  if (state.quote.limit !== data.results.limit) {
    changedQuoteFields.push('limit');
  }

  if (
    state.quote.modifiedCoveragePremium !== data.results.modifiedCoveragePremium
  ) {
    changedQuoteFields.push('modifiedCoveragePremium');
  }

  if (state.quote.srPercentage !== srPercentage) {
    changedQuoteFields.push('srPercentage');
  }

  if (state.quote.srModifier !== srModifier) {
    changedQuoteFields.push('srModifier');
  }

  if (state.quote.erMaxFusionScoreModifier !== erMaxFusionScoreModifier) {
    changedQuoteFields.push('erMaxFusionScoreModifier');
  }

  if (state.quote.erMinFusionScoreModifier !== erMinFusionScoreModifier) {
    changedQuoteFields.push('erMinFusionScoreModifier');
  }

  if (state.quote.erRatedFusionScoreModifier !== erRatedFusionScoreModifier) {
    changedQuoteFields.push('erRatedFusionScoreModifier');
  }

  if (state.quote.erFusionScoreModifier !== erFusionScoreModifier) {
    changedQuoteFields.push('erFusionScoreModifier');
  }

  if (state.quote.erFusionScore !== erFusionScore) {
    changedQuoteFields.push('erFusionScore');
  }

  if (state.quote.stcMinModifier !== stcMinModifier) {
    changedQuoteFields.push('stcMinModifier');
  }

  if (state.quote.stcMaxModifier !== stcMaxModifier) {
    changedQuoteFields.push('stcMaxModifier');
  }

  if (state.quote.stcRatedModifier !== stcRatedModifier) {
    changedQuoteFields.push('stcRatedModifier');
  }

  if (state.quote.stcModifier !== stcModifier) {
    changedQuoteFields.push('stcModifier');
  }

  state.quote = {
    ...state.quote,
    hasChanged: state.quote.hasChanged || changedQuoteFields.length > 0,
    minimumPremium: data.results.minimumPremium,
    limit: data.results.limit,
    modifiedCoveragePremium: data.results.modifiedCoveragePremium,
    selectedQuotedPremium: data.results.selectedQuotedPremium,
    quotedPremium: data.results.quotedPremium,
    technicalPremium: data.results.technicalPremium,
    ratedPremium: data.results.ratedPremium,
    quotedRatedRatio: data.results.quotedRatedRatio,
    /* Coverages */
    coverages: updateCoveragesFromRater(state, data),

    /* Endorsements */
    endorsements: updateEndorsementsFromRater(state, data),

    /* Schedule rating */
    srPercentage,
    srModifier,
    srMinPercentage,
    srMaxPercentage,
    srMinItemPercentage,
    srMaxItemPercentage,

    /* Enterprise rating */
    erMaxFusionScoreModifier,

    erMinFusionScoreModifier,

    erRatedFusionScoreModifier,

    erFusionScoreModifier,

    erFusionScore,

    /* Significant terms & conditions */
    stcMinModifier,
    stcMaxModifier,
    stcRatedModifier,
    stcModifier,
  };

  if (data.results.excess && data.results.excess.tower) {
    if (
      state.quote.excessTowerLayers.length !== data.results.excess.tower.length
    ) {
      convergeLogger.error({
        message: 'Mismatch in excess tower length',
        state: state.quote.excessTowerLayers,
        data: data.results.excess.tower,
      });
      captureException(
        new Error(
          `Mismatch in excess tower length. Expected ${state.quote.excessTowerLayers.length} but got ${data.results.excess.tower.length}`
        )
      );
    }

    const {
      convergeAttachmentPoint: excessConvergeAttachmentPoint,
      convergeIncreasedLimitFactor: excessConvergeIncreasedLimitFactor,
      convergeLimit: excessConvergeLimit,
      convergePartOfLimit: excessConvergePartOfLimit,
      convergeQuotaShare: excessConvergeQuotaShare,
      convergeQuotedPremium: excessConvergeQuotedPremium,
      convergeQuotedToBenchmarkRatio: excessConvergeQuotedToBenchmarkRatio,
      convergeRatedPremium: excessConvergeRatedPremium,
      convergeUnderlyingPremiumPercent: excessConvergeUnderlyingPremiumPercent,
      correctiveFactor: excessCorrectiveFactor,
      maxCorrectiveFactor: excessMaxCorrectiveFactor,
      minCorrectiveFactor: excessMinCorrectiveFactor,
      convergeQuotedRatedRatio: excessConvergeQuotedRatedRatio,
      scheduleRating: {
        percentage: srExcessPercentage,
        modifier: srExcessModifier,
      },
      significantTermsConditions: {
        modifier: stcExcessModifier,
        maxModifier: stcExcessMaxModifier,
        minModifier: stcExcessMinModifier,
        ratedModifier: stcExcessRatedModifier,
      },
    } = data.results.excess;

    state.quote = {
      ...state.quote,
      excessTowerLayers: state.quote.excessTowerLayers.map(
        (layer, layerIndex) => {
          if (!data.results.excess) {
            return layer;
          }

          const updatedLayer = data.results.excess.tower.find(
            (result) => layer.position === result.position
          );

          if (!updatedLayer) {
            const errorDetails = {
              message: `Could not find updated data for layer ${
                layer.position
              } in: ${
                data.results.excess.tower.map((l) => l.position).join(', ') ??
                '[]'
              }`,
              layer,
              updatedLayer,
            };
            convergeLogger.error(errorDetails);
            captureException(new Error(errorDetails.message), {
              contexts: {
                error: errorDetails,
              },
            });
            return layer;
          }

          const hasLayerChanged =
            layer.hasChanged ||
            layer.attachmentPoint !== updatedLayer.attachmentPoint ||
            layer.minimumPremium !== updatedLayer.minimumPremium ||
            layer.benchmarkPremium !== updatedLayer.benchmarkPremium ||
            layer.boundUnderlyingPercent !==
              updatedLayer.boundUnderlyingPercent ||
            layer.boundIncreasedLimitFactor !==
              updatedLayer.boundIncreasedLimitFactor ||
            layer.companyShare !== updatedLayer.companyShare;

          let foundCompanyShare = 0;

          const newLayer: IUnderwritingQuoteExcessTowerLayer = {
            ...layer,
            hasChanged: hasLayerChanged,
            // companyShare:
            //   typeof updatedLayer.companyShare === 'number'
            //     ? updatedLayer.companyShare
            //     : 0,
            attachmentPoint: updatedLayer.attachmentPoint,
            minimumPremium: updatedLayer.minimumPremium,
            benchmarkPremium: updatedLayer.benchmarkPremium,
            // boundPremium: updatedLayer.boundPremium,
            boundUnderlyingPercent: updatedLayer.boundUnderlyingPercent,
            boundIncreasedLimitFactor: updatedLayer.boundIncreasedLimitFactor,
            companyShare: updatedLayer.companyShare,
            participants: layer.participants.map(
              (participant): IUnderwritingQuoteExcessLayerParticipant => {
                const updatedParticipant = updatedLayer.participants.find(
                  (result) => participant.uuid === result.uuid
                );

                if (!updatedParticipant) {
                  const errorDetails = {
                    message: `Could not find updated participant ${
                      participant.uuid
                    } in ${
                      updatedLayer.participants.map((p) => p.uuid).join(', ') ??
                      '[]'
                    } in layer ${updatedLayer.position}`,
                    layer,
                    updatedLayer: {
                      ...updatedLayer,
                    },
                    participants: [
                      ...updatedLayer.participants.map((p) => p.uuid),
                    ],
                    participant,
                  };
                  convergeLogger.error(errorDetails);
                  handleError({
                    source: 'receiveRaterMessageAndUpdateState',
                    message: errorDetails.message,
                  });
                  return participant;
                }

                if (
                  layer.primary &&
                  layer.boundPremium &&
                  updatedParticipant.boundPremium &&
                  updatedParticipant.boundPremium !== layer.boundPremium
                ) {
                  handleError({
                    source: 'receiveRaterMessageAndUpdateState',
                    message: `React Context participant bound premium differs from Rater participant bound premium for submission ${state.submission.uuid} for quote ${state.quote.uuid}. layer.primary : ${layer.boundPremium} : layer.boundPremium : ${layer.boundPremium} : updatedParticipant.boundPremium : ${updatedParticipant.boundPremium}.`,
                    context: {
                      layer: {
                        layer,
                        updatedLayer,
                      },
                      participant: {
                        participant,
                        updatedParticipant,
                      },
                    },
                  });
                }

                if (isConverge(participant)) {
                  if (
                    updatedParticipant.attachmentPoint !==
                    data.results.excess?.convergeAttachmentPoint
                  ) {
                    handleError({
                      throwError: config.isNodeEnvDevelopment,
                      source: 'receiveRaterMessageAndUpdateState',
                      message: `React Context participant bound premium differs from Rater participant bound premium for submission ${state.submission.uuid} for quote ${state.quote.uuid}. participant.quote?.attachmentPoint: ${participant.quote?.attachmentPoint} : updatedParticipant.attachmentPoint: (${updatedParticipant.attachmentPoint}`,
                    });
                  }

                  if (
                    updatedParticipant.limit !==
                    data.results.excess?.convergeLimit
                  ) {
                    handleError({
                      throwError: config.isNodeEnvDevelopment,
                      source: 'receiveRaterMessageAndUpdateState',
                      message: `React Context participant bound premium differs from Rater participant bound premium for submission ${state.submission.uuid} for quote ${state.quote.uuid}. participant.quote?.limit : ${participant.quote?.limit} : updatedParticipant.limit: (${updatedParticipant.limit}`,
                    });
                  }

                  if (
                    updatedParticipant.boundIncreasedLimitFactor !==
                    data.results.excess?.convergeIncreasedLimitFactor
                  ) {
                    handleError({
                      throwError: config.isNodeEnvDevelopment,
                      source: 'receiveRaterMessageAndUpdateState',
                      message: `React Context participant bound premium differs from Rater participant bound premium for submission ${state.submission.uuid} for quote ${state.quote.uuid}. participant.quote?.boundIncreasedLimitFactor : ${participant.quote?.boundIncreasedLimitFactor}) : updatedParticipant.boundIncreasedLimitFactor : ${updatedParticipant.boundIncreasedLimitFactor}`,
                    });
                  }

                  if (
                    updatedParticipant.companyShare !==
                    data.results.excess?.convergeQuotaShare
                  ) {
                    handleError({
                      throwError: config.isNodeEnvDevelopment,
                      source: 'receiveRaterMessageAndUpdateState',
                      message: `React Context participant bound premium differs from Rater participant bound premium for submission ${state.submission.uuid} for quote ${state.quote.uuid}. participant.quote?.companyShare : ${participant.quote?.companyShare}) : updatedParticipant.companyShare : ${updatedParticipant.companyShare}`,
                    });
                  }
                }

                const hasParticipantChanged =
                  participant.hasChanged ||
                  participant.quote?.attachmentPoint !==
                    updatedParticipant.attachmentPoint ||
                  participant.quote?.minimumPremium !==
                    updatedParticipant.minimumPremium ||
                  participant.quote?.boundPremium !==
                    updatedParticipant.boundPremium ||
                  participant.quote?.benchmarkPremium !==
                    updatedParticipant.benchmarkPremium ||
                  participant.quote?.boundUnderlyingPercent !==
                    updatedParticipant.boundUnderlyingPercent ||
                  participant.quote?.boundIncreasedLimitFactor !==
                    updatedParticipant.boundIncreasedLimitFactor ||
                  participant.quote?.companyShare !==
                    updatedParticipant.companyShare;

                foundCompanyShare += updatedParticipant.companyShare ?? 0;

                const newParticipant: IUnderwritingQuoteExcessLayerParticipant =
                  {
                    ...participant,
                    hasChanged: hasParticipantChanged,
                    quote: {
                      ...participant.quote,
                      uuid: participant.quote?.uuid || '',
                      companyShare:
                        typeof updatedParticipant.companyShare === 'number'
                          ? updatedParticipant.companyShare
                          : 0,
                      attachmentPoint: updatedParticipant.attachmentPoint,
                      minimumPremium: updatedParticipant.minimumPremium,
                      benchmarkPremium: updatedParticipant.benchmarkPremium,
                      // TODO: Remove this layer.primary check, because we should be able to trust the rater response
                      boundPremium: layer.primary
                        ? layer.boundPremium
                        : updatedParticipant.boundPremium || null,
                      boundUnderlyingPercent:
                        updatedParticipant.boundUnderlyingPercent,
                      boundIncreasedLimitFactor:
                        updatedParticipant.boundIncreasedLimitFactor,
                    },
                  };

                convergeLogger.info(
                  `Updating participant ${participant.uuid} in layer ${layer.position}`,
                  {
                    participant,
                    updatedParticipant,
                    newParticipant,
                  }
                );

                if (
                  layerIndex > 0 &&
                  !isNotNullOrUndefined(newParticipant.quote?.minimumPremium)
                ) {
                  const errorDetails = {
                    message: `Minimum premium is null or undefined for participant ${participant.uuid} in layer ${layer.position}`,
                    layer,
                    updatedLayer,
                    newParticipant,
                    participant,
                  };
                  convergeLogger.warn(errorDetails);
                  captureException(new Error(errorDetails.message), {
                    contexts: {
                      error: errorDetails,
                    },
                  });
                  return newParticipant;
                }

                return newParticipant;
              }
            ),
          };

          // Since we're using truncated floats, we need to allow for some rounding error:
          const roundingError = 0.3;

          if (
            // 0.99 < 0.97 && 0.99 > 0.99
            foundCompanyShare < 1 - roundingError &&
            foundCompanyShare > 1 + roundingError
          ) {
            const errorMessage = `The rater returned a combined company share of ${foundCompanyShare} (${layer.participants
              .map((participant) => participant.quote?.companyShare)
              .join(' + ')}) for layer ${layer.position}, but it should be 1.`;

            state.raterError = errorMessage;

            handleError({
              throwError: config.isNodeEnvDevelopment,
              source: 'receiveRaterMessageAndUpdateState',
              message: errorMessage,
              context: {
                raterData: {
                  layer: updatedLayer,
                },
                frontendData: {
                  layer,
                },
                lastMessage: {
                  ...state.messagesSent[state.messagesSent.length - 1],
                },
              },
            });
          }

          return newLayer;
        }
      ),
      excessConvergeAttachmentPoint,
      excessConvergeIncreasedLimitFactor,
      excessConvergeLimit,
      excessConvergePartOfLimit,
      excessConvergeQuotaShare,
      excessConvergeQuotedPremium,
      excessConvergeQuotedToBenchmarkRatio,
      excessConvergeRatedPremium,
      excessConvergeUnderlyingPremiumPercent,
      excessCorrectiveFactor,
      excessMaxCorrectiveFactor,
      excessMinCorrectiveFactor,
      excessConvergeQuotedRatedRatio,
      srExcessModifier,
      srExcessPercentage,
      stcExcessMaxModifier,
      stcExcessMinModifier,
      stcExcessModifier,
      stcExcessRatedModifier,
    };
  }

  for (const field of changedQuoteFields) {
    if (!field) {
      throw new Error(`Invalid field: ${field}`);
    }
    state.changedFields.push(field);
  }

  state.changedFields = [...new Set(state.changedFields)];
};

function updateEndorsementsFromRater(
  state: IRaterContextState,
  data: IUnderwritingSocketOutput
): IUnderwritingEndorsement[] {
  return state.quote.endorsements.map(
    (endorsement): IUnderwritingEndorsement => {
      endorsement = addMetadataToEndorsements(endorsement);

      const updatedData = data.results.endorsements.find(
        (e) => e.uuid === endorsement.uuid
      );
      if (updatedData) {
        const getProperty = getEndorsementRaterPropertyOrFallback(
          updatedData,
          endorsement
        );
        const newEndorsement: IUnderwritingEndorsement = {
          ...endorsement,
          quote: {
            ...endorsement.quote,
            fusionScoreModifier:
              endorsement.quote.selectedFusionScoreModifier != null
                ? endorsement.quote.selectedFusionScoreModifier
                : getProperty('fusionScoreModifier'),
            ratedFusionScoreModifier: getProperty('ratedFusionScoreModifier'),
            minFusionScoreModifier: getProperty('minFusionScoreModifier'),
            maxFusionScoreModifier: getProperty('maxFusionScoreModifier'),
            basePremium: getProperty('basePremium'),
            technicalPremium: getProperty('technicalPremium'),
            modifiedCoveragePremium: getProperty('modifiedCoveragePremium'),
            // limit: getProperty('limit'),
            // retention: getProperty('retention'),
            fusionScore: getProperty('fusionScore'),
            limitRetentionFactor: getProperty('limitRetentionFactor'),
            policyTermFactor: getProperty('policyTermFactor'),
            priorActsFactor: getProperty('priorActsFactor'),
            restorationPeriodFactor: getProperty('restorationPeriodFactor'),
            waitingPeriodFactor: getProperty('waitingPeriodFactor'),
            commissionAdjustmentFactor: getProperty(
              'commissionAdjustmentFactor'
            ),
            sharedCoverageAggregateFactor: getProperty(
              'sharedCoverageAggregateFactor'
            ),
            sharedPolicyAggregateFactor: getProperty(
              'sharedPolicyAggregateFactor'
            ),
            enhancedMediaFactor: getProperty('enhancedMediaFactor'),
            subCoverageFactor: getProperty('subCoverageFactor'),
            hazardGroup: getProperty('hazardGroup'),
            waitingPeriodHours: getProperty('waitingPeriodHours'),
            restorationPeriodDays: getProperty('restorationPeriodDays'),
          },
        };

        newEndorsement.hasChanged =
          newEndorsement.hasChanged ||
          endorsement.quote.fusionScore !== newEndorsement.quote.fusionScore ||
          endorsement.quote.hazardGroup !== newEndorsement.quote.hazardGroup ||
          endorsement.quote.fusionScoreModifier !==
            newEndorsement.quote.fusionScoreModifier ||
          endorsement.quote.technicalPremium !==
            newEndorsement.quote.technicalPremium ||
          endorsement.quote.enhancedMediaFactor !==
            newEndorsement.quote.enhancedMediaFactor ||
          endorsement.quote.basePremium !== newEndorsement.quote.basePremium;

        return newEndorsement;
      }
      return endorsement;
    }
  );
}

function updateCoveragesFromRater(
  state: IRaterContextState,
  data: IUnderwritingSocketOutput
): IUnderwritingCoverage[] {
  return sortCoveragesByType([
    ...state.quote.coverages.map((coverage): IUnderwritingCoverage => {
      coverage = addMetadataToCoverage(coverage);

      const updatedData = data.results.coverages.find(
        (result) => coverage.uuid === result.uuid
      );

      if (updatedData) {
        return {
          ...coverage,
          hasChanged:
            coverage.hasChanged ||
            coverage.quote.hazardGroup !== updatedData.hazardGroup ||
            coverage.quote.basePremium !== updatedData.basePremium ||
            coverage.quote.technicalPremium !== updatedData.technicalPremium ||
            coverage.quote.modifiedCoveragePremium !==
              updatedData.modifiedCoveragePremium ||
            coverage.quote.fusionScore !== updatedData.fusionScore ||
            coverage.quote.ratedFusionScoreModifier !==
              updatedData.ratedFusionScoreModifier ||
            coverage.quote.modifiedCoveragePremium !==
              updatedData.modifiedCoveragePremium,
          endorsements: updatedData.endorsements,
          quote: {
            ...coverage.quote,
            minFusionScoreModifier:
              updatedData.minFusionScoreModifier ??
              coverage.quote.minFusionScoreModifier,
            maxFusionScoreModifier:
              updatedData.maxFusionScoreModifier ??
              coverage.quote.maxFusionScoreModifier,
            basePremium: updatedData.basePremium ?? coverage.quote.basePremium,
            technicalPremium:
              updatedData.technicalPremium ?? coverage.quote.technicalPremium,
            modifiedCoveragePremium:
              updatedData.modifiedCoveragePremium ??
              coverage.quote.modifiedCoveragePremium,
            // limit: updatedData.limit ?? coverage.quote.limit,
            // retention: updatedData.retention ?? coverage.quote.retention,
            fusionScore: updatedData.fusionScore ?? coverage.quote.fusionScore,
            fusionScoreModifier:
              updatedData.fusionScoreModifier ??
              coverage.quote.fusionScoreModifier,
            ratedFusionScoreModifier:
              updatedData.ratedFusionScoreModifier ??
              coverage.quote.ratedFusionScoreModifier,
            limitRetentionFactor:
              updatedData.limitRetentionFactor ??
              coverage.quote.limitRetentionFactor,
            policyTermFactor:
              updatedData.policyTermFactor ?? coverage.quote.policyTermFactor,
            priorActsFactor:
              updatedData.priorActsFactor ?? coverage.quote.priorActsFactor,
            restorationPeriodFactor:
              updatedData.restorationPeriodFactor ??
              coverage.quote.restorationPeriodFactor,
            waitingPeriodFactor:
              updatedData.waitingPeriodFactor ??
              coverage.quote.waitingPeriodFactor,
            commissionAdjustmentFactor:
              updatedData.commissionAdjustmentFactor ??
              coverage.quote.commissionAdjustmentFactor,
            sharedCoverageAggregateFactor:
              updatedData.sharedCoverageAggregateFactor ??
              coverage.quote.sharedCoverageAggregateFactor,
            sharedPolicyAggregateFactor:
              updatedData.sharedPolicyAggregateFactor ??
              coverage.quote.sharedPolicyAggregateFactor,
            enhancedMediaFactor:
              updatedData.enhancedMediaFactor ??
              coverage.quote.enhancedMediaFactor,
            subCoverageFactor:
              updatedData.subCoverageFactor ?? coverage.quote.subCoverageFactor,
            hazardGroup: updatedData.hazardGroup ?? coverage.quote.hazardGroup,
            waitingPeriodHours:
              updatedData.waitingPeriodHours ??
              coverage.quote.waitingPeriodHours,
            waitingPeriodDays:
              updatedData.waitingPeriodDays ?? coverage.quote.waitingPeriodDays,
            restorationPeriodDays:
              updatedData.restorationPeriodDays ??
              coverage.quote.restorationPeriodDays,
          },
          name: coverage.name,
        };
      }
      return coverage;
    }),
  ]);
}
